/**
 * UJP copy set — the ONE place every Indonesian string of the UJP surfaces
 * lives (assessment §14.5 / DD13, PRD "UI contract" + CR-1 additions). Pages,
 * wizard, panel and tests read from here so wording never drifts between the
 * screen and its assertion.
 */

import type { HariCode, ShiftCode, TariffTipe } from '@/services/api/tariffs';
import type {
  UjpAmendmentErrorCode,
  UjpAmendmentStatus,
  UjpEMoney,
  UjpEnergyType,
  UjpFuelType,
  UjpRateSource,
  UjpRevenueNoteCode,
  UjpRevenueStatus,
  UjpStatus,
} from '@/services/api/ujp';

export const UJP_COPY = {
  page: {
    eyebrow: 'Logistic · UJP',
    title: 'Pengajuan UJP',
    subtitle:
      'Uang jalan 4W: ajukan biaya per trip, finance menyetujui dan shipment Direct 4W dibuat dari pengajuan yang disetujui.',
    create: 'Buat UJP',
    refresh: 'Refresh',
    config: 'Konfigurasi client',
    tariffs: 'Tarif & margin',
    masters: 'Master',
    revenueMissing: 'Revenue?',
    marginPrefix: 'M',
    searchPlaceholder: 'Cari ref, client, driver, plat, origin, tujuan…',
    clientFilter: 'Semua client',
    columns: {
      ref: 'Ref',
      client: 'Client',
      deliveryDate: 'Tgl kirim',
      route: 'Rute',
      driver: 'Driver / Vendor',
      total: 'Total',
      status: 'Status',
      age: 'Umur',
    },
    empty: 'Belum ada pengajuan UJP',
    emptyHint: 'Pengajuan baru muncul di sini setelah diajukan.',
    filteredEmpty: (q: string) => `Tidak ada UJP untuk “${q}”`,
    clearFilter: 'Hapus filter',
    loadFailed: 'Gagal memuat',
    retry: 'Coba lagi',
    reverse: 'Reverse',
    hidden: 'Disembunyikan',
  },

  tabs: {
    SUBMITTED: 'Menunggu',
    APPROVED: 'Disetujui',
    REJECTED: 'Ditolak',
    CANCELLED: 'Dibatalkan',
    ALL: 'Semua',
  } satisfies Record<UjpStatus | 'ALL', string>,

  status: {
    SUBMITTED: 'Menunggu persetujuan',
    APPROVED: 'Disetujui',
    REJECTED: 'Ditolak',
    CANCELLED: 'Dibatalkan',
  } satisfies Record<UjpStatus, string>,

  wizard: {
    title: 'Buat Pengajuan UJP',
    subtitle: 'Uang jalan 4W: info, rute, biaya, driver, lalu review.',
    steps: ['Info', 'Rute', 'Biaya', 'Driver', 'Review'] as const,
    stepOf: (n: number, total: number, name: string) =>
      `Langkah ${n}/${total} · ${name}`,
    back: 'Kembali',
    cancel: 'Batal',
    next: 'Lanjut',
    submit: 'Ajukan UJP',
    submitting: 'Mengajukan…',
    // ── Edit mode (CR-3) ──────────────────────────────────────────────────
    editTitle: (ref: string) => `Ubah ${ref}`,
    saveChanges: 'Simpan perubahan',
    savingChanges: 'Menyimpan…',
    resubmit: 'Ajukan ulang',
    resubmitting: 'Mengajukan ulang…',
    rejectionBanner: (label: string) => `Ditolak: ${label}`,
    rejectionBannerBody: 'Perbaiki bagian yang bermasalah lalu ajukan ulang.',
    pendingRef: 'UJP-········-····',
    pendingRefHint: 'Nomor ditetapkan server saat diajukan',
    edit: 'Ubah',
    completeness: 'Kelengkapan',
    completenessOk: 'Semua bagian lengkap.',
    completenessMissing: (items: string[]) => `Belum lengkap: ${items.join(', ')}.`,
  },

  info: {
    client: 'Client',
    deliveryDate: 'Tanggal kirim',
    serviceType: 'Layanan',
    deliveryType: 'Tipe pengiriman',
    single: 'Single drop',
    multi: 'Multi drop',
    shift: 'Shift',
    reverse: 'Perjalanan reverse',
    reverseHint: 'Menambah "Biaya reverse (client)" ke Transfer bila client punya tarifnya.',
    reverseAuto: 'Otomatis aktif karena rute punya leg Kembali ke pool.',
    ring: 'Ring tarif',
    ringPlaceholder: 'Pilih ring…',
    ringHint: 'Client PER_RING. Disarankan dari tujuan; approver bisa ubah sebelum menyetujui.',
    ringEmpty: 'Belum ada ring untuk client ini.',
  },

  route: {
    saved: 'Rute tersimpan',
    savedEmpty: (client: string) => `Belum ada rute tersimpan untuk ${client}`,
    savedLoadFailed: 'Gagal memuat rute tersimpan',
    manual: 'Buat rute manual',
    plan: 'Rute',
    planPlaceholder: 'Pilih rute dari Route Planner…',
    newPlan: 'Buat rute baru',
    planCaption: 'Rute dikelola di Route Planner. Pilih satu atau buat baru.',
    /** The picked plan's free-text note, read-only under the picker. */
    planNotes: 'Catatan rute',
    summary: 'Ringkasan rute',
    /** CR-5: the workflow pinned on the plan stop (read-only here). */
    workflow: 'Workflow',
    /**
     * CR-6: the per-stop kind label ("Pickup · Leg pool", "Drop off · Bongkar")
     * comes from `stopKindLabel()` in `services/api/routeStopKind.ts` — one
     * label set shared with the planner, the 4W step and the workflow master.
     */
    columns: { place: 'Nama tempat', address: 'Alamat', leg: 'Leg (km)' },
    kmAllLegs: 'KM semua leg',
    kmCharged: 'KM ditagih',
    kmProposed: 'KM diajukan',
    kmProposedHint: 'Diisi dari KM ditagih (leg pool ikut dihitung bila ditagih). Bisa diubah.',
    kmAllLegsHint: 'Σ semua leg termasuk pool.',
    kmChargedHint: 'Σ leg yang kedua ujungnya ditagih.',
    chargedRule: 'Aturan client: leg pool ditagih',
    unchargedRule: 'Aturan client: leg pool tidak ditagih',
    ruleCaption:
      'Dari Konfigurasi UJP client. Tiap stop pool bisa diatur berbeda di Route Planner.',
    // ── Per-stop billing (CR-8) ───────────────────────────────────────────
    chargedControl: 'Ditagih ke client',
    chargedFollowRule: 'Ikuti aturan client',
    chargedYes: 'Ditagih',
    chargedNo: 'Tidak ditagih',
    chargedOverride: 'Beda dari aturan client',
  },

  cost: {
    payee: 'Payee & kendaraan',
    payeeDriver: 'Driver',
    payeeSubcon: 'Subcon',
    vendor: 'Vendor subcon',
    vendorPlaceholder: 'Cari vendor…',
    vendorEmpty: 'Vendor tidak ditemukan',
    nominalTransfer: 'Nominal transfer',
    plate: 'Plat nomor',
    platePlaceholder: 'Cari plat / pilih kendaraan…',
    plateEmpty: 'Belum ada kendaraan',
    plateManualHint: 'Tidak ada di master? Ketik plat manual.',
    unitType: 'Jenis unit',
    energyType: 'Energi',
    fuelType: 'Jenis BBM',
    baseline: 'Baseline (km/L)',
    konsumsi: 'Konsumsi (kWh/km)',
    energyPriceFuel: 'Harga BBM (Rp/L)',
    energyPriceEv: 'Harga listrik (Rp/kWh)',
    fromMaster: 'dari master',
    edited: 'diubah manual',
    eMoney: 'E-money',
    eMoneyOptions: {
      NONE: 'Tidak ada',
      FLAZZ: 'Flazz',
      QRIS: 'QRIS',
      FLAZZ_QRIS: 'Flazz + QRIS',
    } satisfies Record<UjpEMoney, string>,
    eMoneyCardNo: 'Nomor kartu e-money',
    eMoneyCardNoPlaceholder: 'mis. 6013 5010 1234 5678',
    eMoneyCardNoHint:
      'Catatan nomor kartu untuk trip ini (kartu berpindah antar truk).',
    /** A subcon UJP's driver line: the placeholder account, closed by ops. */
    subconDriver: 'Driver Subcon · diselesaikan ops lewat Mark as complete',
    operational: 'Biaya operasional (uang jalan)',
    /** How a slot's pocket is decided (server-side, by the slot's name). */
    operationalHint:
      'Kosongkan biaya yang tidak ada. Toll dan parkir tap dibayar lewat Flazz bila kendaraan punya Flazz; selain itu transfer.',
    paidBy: (payment: 'FLAZZ' | 'TRANSFER') =>
      `Dibayar: ${payment === 'FLAZZ' ? 'Flazz' : 'Transfer'}`,
    justification: 'Catatan biaya (opsional)',
    justificationPlaceholder: 'mis. kawalan diminta client untuk barang bernilai tinggi',
    /** Freshness of costs pre-filled from a route plan (R7). */
    fromPlan: (plan: string, date: string, by: string | null) =>
      `Biaya dari rute ${plan} · diperbarui ${date}${by ? ` oleh ${by}` : ''}`,
    /** Typed costs hide the plan's costs; this applies them on purpose (R1). */
    applyPlan: 'Terapkan biaya dari rute',
    applyPlanHint: (plan: string) =>
      `Rute ${plan} punya biaya standar. Biaya di bawah sudah diubah, jadi tidak ditimpa.`,
    overrideBbm: 'Override BBM',
    overrideBbmHint: 'Pakai nominal BBM tetap, bukan liter × harga.',
    bbmFix: 'BBM tetap',
    disabledForSubcon: 'Tidak dipakai untuk subcon',
    breakdown: 'Rincian estimasi',
    kmWithMargin: (pct: number) => `KM + margin ${pct}%`,
    liters: 'Estimasi liter',
    bbmCost: 'Biaya BBM',
    reverseLine: 'Biaya reverse (client)',
  },

  estimate: {
    total: 'Estimasi total',
    flazz: 'Flazz',
    qris: 'Uang jalan QRIS',
    qrisShort: 'QRIS',
    transfer: 'Transfer',
    stale: 'Estimasi belum diperbarui',
    failed: 'Estimasi gagal',
    retry: 'Coba lagi',
    server: 'Dihitung server',
    pending: 'Menghitung…',
    idle: 'Lengkapi rute & biaya untuk estimasi',
  },

  driver: {
    section: 'Driver',
    vendorDriver: 'Nama driver vendor',
    vendorDriverHint: 'Opsional untuk subcon.',
    bankDriver: 'Rekening driver',
    bankSubcon: 'Rekening subcon',
    bankName: 'Bank',
    accountNumber: 'No. rekening',
    accountHolder: 'Atas nama',
    bankPrefilled: 'Diisi dari data driver. Bisa diubah.',
    bankPrefillEmpty: 'Driver ini belum punya rekening di master; isi manual.',
    bankPrefillFailed: 'Data driver tidak bisa dimuat; isi rekening manual.',
  },

  panel: {
    /** The UJP's optional "Catatan biaya" (PLAN-UJP-DYNAMIC-COSTS D3). */
    costNote: 'Catatan biaya',
    /** Edit-history field names a person reads (others show as sent). */
    historyFieldName: {
      costItems: 'Biaya',
      justifikasiBiayaLainLain: 'Catatan biaya',
    } as Record<string, string>,
    requester: (email: string) => `diajukan oleh ${email}`,
    waitingFinance: 'Menunggu persetujuan finance',
    totalLabel: 'Estimasi total uang jalan',
    createdOnApprove: 'Dibuat saat disetujui',
    notCreated: 'Belum dibuat',
    viewShipment: (waybill: string) => `Lihat shipment ${waybill}`,
    direct4w: 'Direct 4W',
    stopsShipped: (n: number) => `${n} stop`,
    /** CR8-D5: every stop ships; billing only changes the km. */
    allStopsShipped:
      'Semua stop masuk rute shipment; leg pool yang tidak ditagih hanya tidak dihitung ke km.',
    breakdown: 'Rincian biaya',
    bankDriver: 'Rekening driver',
    bankSubcon: 'Rekening subcon',
    copy: 'Salin',
    copied: 'Nomor rekening disalin',
    hidden: 'Disembunyikan',
    maskedTitle: 'Hanya requester & approver dapat melihat',
    stops: 'Stops',
    history: 'Riwayat',
    approve: 'Setujui & buat shipment',
    approving: 'Menyetujui…',
    reject: 'Tolak',
    rejectTitle: 'Tolak UJP',
    rejecting: 'Menolak…',
    cancel: 'Batalkan pengajuan',
    cancelBody: 'Pengajuan ditandai dibatalkan permanen dan tidak bisa diputuskan lagi.',
    cancelHint: 'Buat pengajuan baru bila trip masih diperlukan.',
    cancelling: 'Membatalkan…',
    // ── Edit-while-open (CR-3) — replaces the "Buat ulang" create-new flow ──
    edit: 'Ubah',
    editResubmit: 'Ubah & ajukan ulang',
    version: (v: number) => `v${v}`,
    updatedTitle: 'Diperbarui oleh requester',
    updatedBody: 'Muat ulang sebelum memutuskan.',
    changesTitle: 'Perubahan',
    changeLine: (field: string, from: string | null, to: string | null) =>
      `${field}: ${from ?? '—'} → ${to ?? '—'}`,
    reason: 'Alasan',
    reasonPlaceholder: 'Pilih alasan…',
    reasonsEmpty: 'Alasan belum dikonfigurasi',
    note: 'Catatan',
    noteRequired: 'Catatan wajib untuk alasan Lainnya',
    notePlaceholder: 'Jelaskan alasannya…',
    alreadyDecided: (name: string) => `Sudah diputuskan oleh ${name}`,
    notFound: 'UJP tidak ditemukan',
    loadFailed: 'Gagal memuat UJP',
    kmMargin: (pct: number) => `KM + margin ${pct}%`,
    reverseLine: 'Biaya reverse (client)',
    vehicle: 'Kendaraan',

    // ── Revenue / margin (CR-4, approver-only) ────────────────────────────
    revenueHeading: 'Revenue & margin',
    revenueLabel: 'Revenue',
    marginLabel: 'Margin',
    revenueUnavailable: 'Revenue belum bisa dihitung',
    revenueStatusHint: {
      MISSING_TARIFF:
        'Tarif belum diatur untuk client ini, atau tidak berlaku di hari/shift pengiriman.',
      UNSUPPORTED_MODE: 'Mode tarif belum didukung (per-body / tier).',
      MISSING_RING: 'Ring belum dipilih untuk trip PER_RING.',
      MISSING_TIER: 'Tier jarak belum diatur untuk tarif ini.',
    } satisfies Record<Exclude<UjpRevenueStatus, 'OK'>, string> as Record<
      string,
      string
    >,
    /** One line per note code (CR4b-D11) — the wire joins them with `,`. */
    revenueNotes: {
      MULTI_RATE_FALLBACK: 'Rate multi-drop 0/kosong — pakai rate single.',
      BODY_RATE_DEFAULT: 'Tipe unit tidak punya rate khusus, memakai rate dasar.',
      TIER_ABOVE_MAX: 'KM melewati tier tertinggi, memakai rate tier tertinggi.',
      RING_AUTO_SINGLE: 'Ring tidak dipilih; dipakai satu-satunya ring aktif client.',
    } satisfies Record<UjpRevenueNoteCode, string>,
    // ── Revenue breakdown from the tariff snapshot (CR-4b §20.3) ──────────
    rateApplied: 'Rate dipakai',
    rateSource: {
      BODY_MULTI: 'rate khusus unit · multi',
      BASE_MULTI: 'rate dasar · multi',
      BODY_SINGLE: 'rate khusus unit · single',
      BASE_SINGLE: 'rate dasar · single',
      TIER: 'tier jarak',
      FIXED: 'fixed',
    } satisfies Record<UjpRateSource, string>,
    rateBody: (key: string) => `unit ${key}`,
    tierHit: (maxKm: string) => `Tier ≤ ${maxKm}`,
    asuransiLine: 'Asuransi',
    surchargeLine: 'Surcharge multidrop',
    surchargeSub: (titik: number, free: number, rate: string) =>
      `${titik} titik · ${free} gratis · ${rate}/titik`,
    reverseRevenueLine: 'Harga reverse',
    komisiLine: 'Komisi driver',
    komisiSub: 'dikurangkan dari margin',
    marginNegative: 'Margin negatif',
    marginConfirmTitle: (ref: string) => `Setujui ${ref} dengan margin negatif?`,
    marginConfirmBody: (margin?: string | null) =>
      margin && margin !== '—'
        ? `Margin trip ini ${margin}. Tetap setujui & buat shipment?`
        : 'Biaya melebihi revenue tarif client (margin negatif). Tetap setujui & buat shipment?',
    marginConfirmCta: 'Setujui walau rugi',
    priceLocked: 'Hanya approver dapat melihat revenue & margin.',

    // ── Shipment preview (CR-D6 / CR-D7) ──────────────────────────────────
    subcon: 'Subcon',
    shipmentHeading: 'Shipment',
    created: 'Dibuat',
    notCreatedFinal: (status: string) => `Pengajuan ${status}.`,
    approveCreates: 'Menyetujui akan membuat shipment.',

    // ── Rincian biaya ─────────────────────────────────────────────────────
    payeeSubcon: 'Payee subcon',
    payeeSubconHint: '100% transfer',
    kmLine: 'KM semua leg / ditagih / diajukan',
    chargedRuleYes: 'leg pool ditagih',
    chargedRuleNo: 'leg pool tidak ditagih',
    bbm: 'BBM',
    energi: 'Energi',
    eMoney: 'E-money',
    eMoneyCardNo: 'Nomor kartu e-money',
    driver: 'Driver',
    vendor: 'Vendor',

    // ── Rute ──────────────────────────────────────────────────────────────
    routeHeading: (n: number) => `Rute · ${n} stop`,
    /** Per pool stop: the effective billing (CR-8). */
    charged: 'ditagih',
    notCharged: 'tidak ditagih',

    // ── Rejection / history ───────────────────────────────────────────────
    rejectedHeading: (label: string) => `Ditolak · ${label}`,
    decidedByAt: (by: string, at: string) => `oleh ${by} · ${at}`,
    historyVerb: {
      SUBMITTED: 'Diajukan',
      APPROVED: 'Disetujui',
      REJECTED: 'Ditolak',
      CANCELLED: 'Dibatalkan',
    } satisfies Record<UjpStatus, string>,
    historyBy: (verb: string, by: string) => `${verb} oleh ${by}`,
    conflictReloaded: 'Data dimuat ulang.',

    // ── Footer hints ──────────────────────────────────────────────────────
    ownRequestHint: 'Requester tidak dapat menyetujui pengajuannya sendiri.',
    notApproverHint: 'Hanya approver (finance) yang dapat memutuskan.',

    // ── Decision modals ───────────────────────────────────────────────────
    rejectSubtitle: 'Requester akan melihat alasan ini dan dapat membuat ulang.',
    reasonHint: 'Dari master alasan (type UJP_REJECTION).',
    noteOptional: 'Catatan (opsional)',
    cancelConfirmTitle: (ref: string) => `Batalkan pengajuan ${ref}?`,
    cancelBack: 'Kembali',
  },

  // ── Biaya tambahan (CR-7) ─────────────────────────────────────────────────
  amendment: {
    section: 'Biaya tambahan',
    empty: 'Belum ada biaya tambahan.',
    create: 'Ajukan biaya tambahan',
    createTitle: (ref: string) => `Ajukan biaya tambahan ${ref}`,
    createSubtitle:
      'Nominal masuk ke Transfer setelah disetujui finance. Satu pengajuan menunggu pada satu waktu.',
    submit: 'Ajukan',
    submitting: 'Mengajukan…',
    amount: 'Nominal',
    justification: 'Justifikasi',
    justificationPlaceholder: 'mis. Tilang di tol Cikampek, bukti menyusul',
    seq: (n: number) => `#${n}`,
    requestedBy: (by: string, at: string) => `diajukan ${by} · ${at}`,
    decidedBy: (verb: string, by: string, at: string) => `${verb} ${by} · ${at}`,
    approvedVerb: 'disetujui',
    rejectedVerb: 'ditolak',
    reasonLine: (code: string) => `Alasan: ${code}`,
    status: {
      SUBMITTED: 'Menunggu persetujuan finance',
      APPROVED: 'Disetujui',
      REJECTED: 'Ditolak',
    } satisfies Record<UjpAmendmentStatus, string>,
    totalWithAmendments: 'Total setelah biaya tambahan',
    amendedSum: 'Biaya tambahan disetujui',
    approve: 'Setujui',
    approving: 'Menyetujui…',
    reject: 'Tolak',
    rejectTitle: 'Tolak biaya tambahan',
    rejectSubtitle: 'Pengaju akan melihat alasan ini di riwayat UJP.',
    /** 409 rule codes from the two endpoints, in Bahasa (CR7-D2/D3). */
    errors: {
      UJP_NOT_APPROVED: 'UJP belum disetujui',
      SHIPMENT_COMPLETED:
        'Shipment sudah selesai; biaya tambahan tidak bisa diajukan lagi',
      AMENDMENT_PENDING:
        'Masih ada biaya tambahan yang menunggu persetujuan',
      AMENDMENT_DECIDED: 'Biaya tambahan ini sudah diputuskan',
    } satisfies Record<UjpAmendmentErrorCode, string>,
    validation: {
      amount: 'Nominal wajib lebih dari 0',
      justification: 'Justifikasi wajib diisi (minimal 3 karakter)',
    },
    /** Riwayat verbs keyed by the history `note` (APPROVED → APPROVED rows). */
    historyVerb: {
      BIAYA_TAMBAHAN_SUBMITTED: 'mengajukan biaya tambahan',
      BIAYA_TAMBAHAN_APPROVED: 'menyetujui biaya tambahan',
      BIAYA_TAMBAHAN_REJECTED: 'menolak biaya tambahan',
    } as Record<string, string>,
    historyLine: (verb: string, seq: number | null, by: string) =>
      `${by} ${verb}${seq != null ? ` #${seq}` : ''}`,
    /** `amendment.<seq>.<key>` history fields, readably. */
    historyField: {
      amount: 'nominal',
      justifikasi: 'justifikasi',
      status: 'status',
      note: 'catatan',
    } as Record<string, string>,
    /** Queue chip on a list row with a SUBMITTED amendment. */
    pendingChip: 'Biaya tambahan menunggu',
    pendingChipCount: (n: number) => `${n} biaya tambahan menunggu`,
  },

  // ── Spend Control (ASSESSMENT-UJP-SPEND-CONTROL design 1A–12A) ────────────
  spend: {
    /** Section 2b of the drawer (1A). */
    heading: 'Pengajuan transfer · Spend Control',
    /** Prefix of the compact line inside an APPROVED biaya tambahan (2A). */
    lineLabel: 'Spend Control:',
    /** Badge per state — always text, colour is never the only signal (10A). */
    badge: {
      NOT_SENT: 'Belum diajukan',
      NOT_REQUIRED: 'Tidak perlu',
      SENDING: 'Mengirim…',
      SENT: 'Terkirim',
      FAILED: 'Gagal',
      UNKNOWN: 'Belum pasti',
    },
    notSent: 'Belum dikirim ke Spend Control.',
    notRequired: 'Tidak ada uang jalan transfer.',
    sending: 'Sedang dikirim ke Spend Control…',
    /** 4A. */
    unknown:
      'Koneksi terputus saat mengirim. Cek di Spend Control dulu sebelum kirim ulang.',
    workflowMissing:
      'Belum ada alur approval di Spend Control untuk OPS/4W. Hubungi tim Basecamp.',
    /** 11A: a non-approver on FAILED / UNKNOWN, in place of the button. */
    askApprover: 'Hubungi approver UJP untuk kirim ulang.',
    /** 8A: the outbound link, separate from the (plain mono) request code. */
    open: 'Buka di Spend Control',
    transferLine: 'Uang jalan transfer',
    send: 'Kirim ke Spend Control',
    resend: 'Kirim ulang',
    /** UNKNOWN: opens the 6A confirmation first. */
    resendConfirm: 'Kirim ulang…',
    busy: 'Mengirim…',
    toastSent: 'Pengajuan dikirim ke Spend Control',
    toastFailed: 'Pengajuan ke Spend Control gagal',
    toastUnknown: 'Pengajuan ke Spend Control belum pasti',
    toastSendFailed: 'Gagal mengirim ke Spend Control',
    // ── 6A: resend an UNKNOWN request (force) ─────────────────────────────
    resendTitle: 'Kirim ulang ke Spend Control?',
    resendSubtitleAmendment: (ref: string, seq: number) =>
      `${ref} · Biaya tambahan #${seq}`,
    resendBody:
      'Pengiriman sebelumnya terputus. Bisa jadi pengajuan sudah masuk ke Spend Control. Kalau dikirim ulang dan ternyata sudah ada, finance akan melihat dua pengajuan untuk UJP ini.',
    search: 'Cari di Spend Control',
    copyRef: 'Salin referensi',
    refCopied: 'Referensi UJP disalin',
    checked: 'Saya sudah cek, belum ada pengajuan untuk UJP ini',
    cancel: 'Batal',
    // ── 7A: the UJP list ──────────────────────────────────────────────────
    chipFailed: 'Spend Control gagal',
    chipUnknown: 'Spend Control belum pasti',
    filter: 'Spend Control bermasalah',
  },

  toast: {
    amendmentSubmitted: (ref: string, seq: number) =>
      `Biaya tambahan #${seq} untuk ${ref} diajukan`,
    amendmentApproved: (ref: string, seq: number) =>
      `Biaya tambahan #${seq} untuk ${ref} disetujui`,
    amendmentRejected: (ref: string, seq: number) =>
      `Biaya tambahan #${seq} untuk ${ref} ditolak`,
    amendmentSubmitFailed: 'Pengajuan biaya tambahan gagal',
    amendmentDecisionFailed: 'Keputusan biaya tambahan gagal',
    submitted: (ref: string) => `${ref} diajukan`,
    submittedView: 'Lihat',
    approved: (ref: string, waybill: string) =>
      `${ref} disetujui · Shipment ${waybill} dibuat`,
    approvedSubcon: (ref: string) => `${ref} disetujui`,
    rejected: (ref: string) => `${ref} ditolak`,
    /** CR5-D3: a plan-pinned workflow the shipment fell back from. */
    workflowFallback: (seq: number, reason: string) =>
      `Workflow stop ${seq} diganti ke default (${reason})`,
    workflowFallbackTitle: (n: number) =>
      `${n} workflow stop diganti ke default`,
    cancelled: 'Pengajuan dibatalkan',
    copied: 'Nomor rekening disalin',
    updated: (ref: string) => `${ref} diperbarui`,
    resubmitted: (ref: string) => `${ref} diajukan ulang`,
    submitFailed: 'Pengajuan gagal',
    decisionFailed: 'Keputusan gagal',
    cancelFailed: 'Pembatalan gagal',
  },

  config: {
    eyebrow: 'Logistic · UJP',
    title: 'Konfigurasi UJP client',
    subtitle:
      'Per client: apakah leg pool (berangkat dari & kembali ke pool) ditagih dan biaya reverse. Berlaku untuk estimasi dan shipment yang dibuat saat UJP disetujui.',
    back: 'Kembali ke UJP',
    readOnly: 'Hanya approver UJP yang dapat mengubah konfigurasi ini.',
    columns: {
      client: 'Client',
      charged: 'Leg pool ditagih',
      reverse: 'Biaya reverse',
      eMoney: 'Default e-money',
      notes: 'Catatan',
      updated: 'Diperbarui',
    },
    notesPlaceholder: 'Catatan singkat…',
    save: 'Simpan',
    saving: 'Menyimpan…',
    saved: (client: string) => `Konfigurasi ${client} disimpan`,
    saveFailed: 'Gagal menyimpan konfigurasi',
    empty: 'Belum ada client',
    loadFailed: 'Gagal memuat konfigurasi',
    unset: 'Belum diatur',
  },

  // ── Tarif & margin page (CR-4 / CR-4b) ────────────────────────────────────
  tariffs: {
    tipe: {
      PER_TRIP_FLAT: 'Flat per trip',
      FIXED: 'Fixed',
      PER_RING: 'Per ring',
      DISTANCE_TIER: 'Tier jarak (KM)',
    } satisfies Record<TariffTipe, string>,
    hari: {
      MON: 'Sen',
      TUE: 'Sel',
      WED: 'Rab',
      THU: 'Kam',
      FRI: 'Jum',
      SAT: 'Sab',
      SUN: 'Min',
    } satisfies Record<HariCode, string>,
    shift: {
      Dawn: 'Subuh',
      Morning: 'Pagi',
      Noon: 'Siang',
      Night: 'Malam',
    } satisfies Record<ShiftCode, string>,
    // Placeholder jam per shift, ditampilkan di picker shift create UJP.
    // ASUMSI (split 6 jam rata) — sesuaikan kalau jam operasional beda.
    shiftHours: {
      Dawn: '00:00–06:00',
      Morning: '06:00–12:00',
      Noon: '12:00–18:00',
      Night: '18:00–24:00',
    } satisfies Record<ShiftCode, string>,
    everyDay: 'setiap hari',
    everyShift: 'semua shift',
    hariOperasi: 'Hari operasi',
    shiftOperasi: 'Shift operasi',
    // Rates
    rateSingle: 'Rate single',
    rateMulti: 'Rate multi',
    rateFixed: 'Rate fixed',
    bodyOverridesSingle: 'Rate khusus per tipe unit (single)',
    bodyOverridesMulti: 'Rate khusus per tipe unit (multi)',
    bodyKey: 'Tipe unit',
    bodyKeyPlaceholder: 'mis. Fuso Tronton',
    bodyRate: 'Rate',
    bodyHint: 'Dicocokkan dengan "Jenis unit" UJP (tanpa peduli huruf besar/kecil). Kosong / 0 = pakai rate dasar.',
    addOverride: 'Tambah tipe unit',
    removeOverride: 'Hapus tipe unit',
    noOverrides: 'Belum ada rate khusus.',
    overridesCount: (n: number) => `${n} rate khusus`,
    // Shares
    shares: 'Porsi driver / helper',
    sharesHint: 'Komisi (biaya), dikurangkan dari margin. Multi 0 = pakai porsi single.',
    driverShare: 'Porsi driver',
    helperShare: 'Porsi helper',
    driverShareMulti: 'Porsi driver (multi)',
    helperShareMulti: 'Porsi helper (multi)',
    sharesSummary: (driver: string, helper: string) => `driver ${driver} · helper ${helper}`,
    // Tiers
    tiers: 'Tier jarak (KM) → rate',
    tierMaxKm: 'KM ≤',
    tierRate: 'Rate',
    tierHint: 'KM diajukan ≤ ambang pertama yang cocok. Di atas tier tertinggi memakai rate tertinggi.',
    addTier: 'Tambah tier',
    removeTier: 'Hapus tier',
    tiersCount: (n: number) => `${n} tier`,
    // Common
    asuransi: 'Asuransi',
    hargaReverse: 'Harga reverse',
    hargaReverseHint: 'Ditambahkan ke revenue bila UJP reverse.',
    multidrop: 'Surcharge multidrop',
    multidropFreePoints: 'Titik gratis',
    multidropExtraRate: 'Rate per titik ekstra',
    multidropHint: 'Titik = jumlah drop. 0 rate = nonaktif.',
    tagihPerRit: 'Tagih per rit',
    tagihPerRitHint:
      'Berlaku di laporan margin (CR-4c), tidak mengubah harga satu UJP',
    catatan: 'Catatan',
    catatanPlaceholder: 'Catatan singkat…',
    berlakuMulai: 'Berlaku mulai',
    berlakuMulaiHint:
      'Dua tarif aktif tidak boleh bertumpang-tindih di tanggal, hari, dan shift yang sama.',
    berlakuSampai: 'Berlaku sampai',
    berlakuSampaiHint: 'Kosong = tanpa batas.',
    keterangan: 'Keterangan',
    keteranganPlaceholder: 'mis. area Karawang & sekitarnya',
    perRingHint: 'Tambah ring lewat "Kelola ring" setelah tarif dibuat.',
    modeUnsupported: 'Mode belum didukung',
    // Ring toggle (TODO-22)
    ringInactive: 'Nonaktif',
    ringDeactivate: 'Nonaktifkan',
    ringActivate: 'Aktifkan',
    ringDeactivated: (nama: string) => `Ring ${nama} dinonaktifkan`,
    ringActivated: (nama: string) => `Ring ${nama} diaktifkan`,
    ringToggleFailed: 'Gagal memperbarui ring',
    // Save toasts
    saveFailed: 'Gagal menyimpan tarif',
    overlapTitle: 'Tarif bertumpang-tindih',
    // Validation (client-side, mirrors the DTO)
    validation: {
      tiersEmpty: 'Minimal satu tier.',
      tierMaxKm: 'KM tier wajib > 0.',
      tiersIncreasing: 'KM tier harus naik dan tidak boleh sama.',
      bodyKeyDuplicate: 'Tipe unit duplikat pada rate khusus.',
      bodyKeyEmpty: 'Tipe unit pada rate khusus wajib diisi.',
      multidropFreePoints: 'Titik gratis wajib angka ≥ 0.',
      berlakuRange: 'Berlaku sampai tidak boleh sebelum berlaku mulai.',
    },
  },

  // ── Masters admin page (TODO-22) ──────────────────────────────────────────
  masters: {
    eyebrow: 'Logistic · UJP',
    title: 'Master UJP',
    subtitle:
      'Kendaraan, harga BBM/energi, dan vendor subcon yang dipakai wizard UJP. Mengubah master tidak mengubah UJP yang sudah diajukan — setiap pengajuan menyimpan nilai yang dipakainya.',
    back: 'Kembali ke UJP',
    readOnly: 'Hanya approver UJP yang dapat mengubah master ini.',
    tabs: {
      vehicles: 'Kendaraan',
      energy: 'Harga BBM/energi',
      vendors: 'Vendor subcon',
    },
    showInactive: 'Tampilkan nonaktif',
    filter: {
      trigger: 'Filter',
      title: {
        vehicles: 'Filter kendaraan',
        energy: 'Filter harga',
        vendors: 'Filter vendor',
      },
      reset: 'Reset',
      apply: 'Terapkan',
    },
    active: 'Aktif',
    inactive: 'Nonaktif',
    deactivate: 'Nonaktifkan',
    activate: 'Aktifkan',
    edit: 'Ubah',
    save: 'Simpan',
    saving: 'Menyimpan…',
    cancel: 'Batal',
    loadFailed: 'Gagal memuat master',
    retry: 'Coba lagi',
    energyType: {
      ICE: 'Diesel/bensin (ICE)',
      EV: 'Listrik (EV)',
    } satisfies Record<UjpEnergyType, string>,
    fuelType: {
      SOLAR: 'Solar',
      DEXLITE: 'Dexlite',
      PERTALITE: 'Pertalite',
      EV_KWH: 'Listrik (kWh)',
    } satisfies Record<UjpFuelType, string>,
    vehicles: {
      searchPlaceholder: 'Cari plat…',
      add: 'Tambah kendaraan',
      createTitle: 'Tambah kendaraan',
      editTitle: (plate: string) => `Ubah ${plate}`,
      columns: {
        plate: 'Plat',
        unitType: 'Jenis unit',
        energy: 'Energi',
        fuel: 'BBM / energi',
        consumption: 'Baseline / konsumsi',
        status: 'Status',
      },
      plate: 'Plat nomor',
      platePlaceholder: 'mis. B 9021 UYT',
      unitType: 'Jenis unit',
      unitTypePlaceholder: 'mis. Colt Diesel Engkel',
      allUnitTypes: 'Semua jenis unit',
      unitTypeEmpty: 'Jenis unit tidak ditemukan',
      energy: 'Energi',
      fuel: 'Jenis BBM / energi',
      baseline: 'Baseline (km/L)',
      konsumsi: 'Konsumsi (kWh/km)',
      eMoney: 'Default e-money',
      eMoneyNone: 'Tidak diatur',
      eMoneyCardNo: 'Nomor kartu e-money',
      eMoneyCardNoPlaceholder: 'mis. 6013 5010 1234 5678',
      eMoneyCardNoHint:
        'Nomor kartu yang nempel di truk ini — otomatis terisi saat bikin UJP. Kalau kartu pindah truk, ubah di sini.',
      baselineHint: 'ICE wajib baseline > 0; EV wajib konsumsi > 0.',
      empty: 'Belum ada kendaraan',
      created: (plate: string) => `Kendaraan ${plate} ditambahkan`,
      updated: (plate: string) => `Kendaraan ${plate} diperbarui`,
      deactivated: (plate: string) => `Kendaraan ${plate} dinonaktifkan`,
      activated: (plate: string) => `Kendaraan ${plate} diaktifkan`,
      saveFailed: 'Gagal menyimpan kendaraan',
      validation: {
        plate: 'Plat nomor wajib diisi',
        fuel: 'Jenis BBM / energi wajib dipilih',
        baseline: 'Kendaraan ICE wajib baseline > 0',
        konsumsi: 'Kendaraan EV wajib konsumsi > 0',
      },
    },
    energy: {
      current: 'Harga berlaku hari ini',
      currentEmpty: 'Belum ada harga',
      history: 'Riwayat harga',
      allFuels: 'Semua jenis',
      add: 'Tambah harga',
      createTitle: 'Tambah harga baru',
      createSubtitle:
        'Harga bertanggal dan tidak bisa diubah — tambahkan harga baru dengan tanggal berlaku.',
      columns: {
        fuel: 'BBM / energi',
        price: 'Harga',
        effectiveFrom: 'Berlaku mulai',
      },
      perLiter: '/L',
      perKwh: '/kWh',
      fuel: 'Jenis BBM / energi',
      price: 'Harga (Rp)',
      effectiveFrom: 'Berlaku mulai',
      effectiveNow: 'Berlaku sekarang',
      empty: 'Belum ada riwayat harga',
      created: (fuel: string, from: string) => `Harga ${fuel} berlaku ${from} ditambahkan`,
      saveFailed: 'Gagal menambah harga',
      validation: {
        price: 'Harga wajib > 0',
        effectiveFrom: 'Tanggal berlaku wajib diisi',
      },
    },
    vendors: {
      searchPlaceholder: 'Cari vendor…',
      add: 'Tambah vendor',
      createTitle: 'Tambah vendor subcon',
      editTitle: (name: string) => `Ubah ${name}`,
      columns: {
        name: 'Vendor',
        city: 'Kota',
        bank: 'Rekening',
        pic: 'PIC',
        status: 'Status',
      },
      name: 'Nama vendor',
      namePlaceholder: 'mis. CV Angkut Jaya',
      city: 'Kota',
      bankName: 'Bank',
      accountNumber: 'No. rekening',
      accountHolder: 'Atas nama',
      picName: 'Nama PIC',
      picPhone: 'HP PIC',
      empty: 'Belum ada vendor',
      created: (name: string) => `Vendor ${name} ditambahkan`,
      updated: (name: string) => `Vendor ${name} diperbarui`,
      deactivated: (name: string) => `Vendor ${name} dinonaktifkan`,
      activated: (name: string) => `Vendor ${name} diaktifkan`,
      saveFailed: 'Gagal menyimpan vendor',
      validation: {
        name: 'Nama vendor wajib diisi',
      },
    },
  },

  validation: {
    required: 'Wajib diisi',
    client: 'Client wajib dipilih',
    deliveryDate: 'Tanggal kirim wajib diisi',
    deliveryDatePast: 'Tanggal kirim tidak boleh sebelum hari ini',
    route: 'Rute wajib minimal dua stop dengan lokasi',
    km: 'KM diajukan wajib > 0',
    // KM diajukan boleh sedikit di atas jarak rute, tapi maksimum 10%.
    kmMax: (routeKm: number) =>
      `KM diajukan maksimum 10% di atas jarak rute (${routeKm} km) — maks ${(
        routeKm * 1.1
      ).toFixed(1)} km`,
    plate: 'Plat nomor wajib diisi',
    unitType: 'Jenis unit wajib diisi',
    baseline: 'Baseline wajib > 0',
    konsumsi: 'Konsumsi wajib > 0',
    energyPrice: 'Harga energi wajib > 0',
    nominalTransfer: 'Nominal transfer wajib > 0',
    vendor: 'Vendor subcon wajib dipilih',
    rider: 'Driver wajib dipilih',
    bank: 'Bank, nomor rekening dan atas nama wajib diisi',
    saveAsName: 'Nama rute wajib diisi bila disimpan',
    estimateFresh: 'Tunggu estimasi terbaru sebelum mengajukan',
  },
} as const;

